import { Injectable, OnDestroy } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { TokenJWT } from 'src/app/core/models/token/token-jwt';
import { TokenService } from 'src/app/core/services/token/token.service';
import { environment } from 'src/environments/environment';
import { Motivations } from '../../models/Motivations';
import { FoodOrder } from '../../models/FoodOrder';
import { FoodSubscription } from '../../models/FoodSubscription';
import { FoodServiceStatus } from '../../models/FoodServiceStatus';

@Injectable({
  providedIn: 'root',
})
export class FoodDataService implements OnDestroy {
  private token: TokenJWT | undefined; // informazioni dell'account
  private tokenSubscription$: Subscription = new Subscription();

  constructor(
    private tokenService: TokenService,
    private toastController: ToastController
  ) {
    this.getToken();
  }

  ngOnDestroy(): void {
    if (this.tokenSubscription$) this.tokenSubscription$.unsubscribe();
  }

  private getToken(): void {
    this.tokenSubscription$ = this.tokenService
      .getToken()
      .subscribe((token) => {
        this.token = token;
      });
  }

  //#region GET

  public async readState(): Promise<FoodServiceStatus> {
      try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/read-state',
        {
          headers: {},
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error('Error getAllAnimalsLatestSubscription', error);
      return { isActive: false , title: '', description: ''  };
    }
  }

  /**
   * Richiede al server tutti gli abbonamenti del tipo richiesto
   * @param tipologia
   */
  public async getAll() : Promise<FoodSubscription[]> {

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/read-all',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body as FoodSubscription[];

    } catch (error) {
      console.error('Error getAllAnimalsLatestSubscription', error);
      return [];
    }
  }

  /**
   * Richiede al server tutti gli abbonamenti del tipo richiesto
   * @param tipologia
   */
  public async getAllOrders(tipologia: string = '', idAnimale: string | null = null) : Promise<FoodOrder[]> {
    const params = idAnimale
      ? new URLSearchParams({ tipologia: tipologia, idAnimale: idAnimale })
      : new URLSearchParams({ tipologia: tipologia });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/read-all' +
          '?' +
          params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body as FoodOrder[];

    } catch (error) {
      console.error('Error getAllAnimalsLatestSubscription', error);
      return [];
    }
  }

  /**
   * Richiede al server l'ordine richiesto
   * @param idOrdine id dell'Ordine richiesto
   */
  public async getOrder(idOrdine: string): Promise<FoodOrder|null> {
    const params = new URLSearchParams({ IDOrdine: idOrdine });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/read-one' +
          '?' +
          params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body as FoodOrder;
    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return null;
    }
  }

  async getSubscriptionState(idAnimale: string) {
    const params = new URLSearchParams({ IDAnimale: idAnimale });

    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/subscription/read-one' +
        '?' +
        params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body.idStato;

    } catch (error) {
      console.error(error);
      return '';
    }
  }

  async getSubscription(idAnimale: string): Promise<FoodSubscription | null> {
    const params = new URLSearchParams({ IDAnimale: idAnimale });

    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/subscription/read-one' +
        '?' +
        params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) return null;

      return data.body as FoodSubscription;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Richiede al server l'ordine successivo dell'animale
   * @param idAnimale id dell'animale richiesto
   */
  public async getNextOrder(idAnimale: string): Promise<FoodOrder|null>{
    const params = new URLSearchParams({ IDAnimale: idAnimale });
    
    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/read-next' +
          '?' +
          params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body as FoodOrder;
    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return null;
    }
  }

  /**
   * Richiede al server tutte le porzioni dell'animale
   * @param idAnimale
   * @param idAbbonamento
   */
  public async getAllAnimalPortion(idAnimale: string | null = null, idAbbonamento: string | null = null, includiProva: boolean = false){
    const params = idAnimale ?
      new URLSearchParams({ IDAnimale: idAnimale }) :
      new URLSearchParams({ IDAbbonamento: idAbbonamento!, IncludiProva: includiProva.toString() });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/portion/read-all' +
          '?' +
          params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
      
    } catch (error) {
      console.error(error);
    }

    return [];
  }

  /**
   * Richiede al server tutti i gusti di cibo
   */
  public async getFoodFlavor(){
    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/flavors/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error(error);
      Promise.resolve(false);
      return [];
    }
  }

  /**
   * Richiede al server tutti i gusti dell'abbonamento
   * @param idOrdine string
   */
  public async getSubscriptionFlavor(idOrdine: string){
    const params = new URLSearchParams({ IDOrdine: idOrdine});

    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/order/subscription/flavors/read' +        
        '?' +
        params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error(error);
      Promise.resolve(false);
      return [];
    }
  }

  /**
   * Richiede al server tutti i faq di cibo
   */
  public async getFaq(){
    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/faq/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return null;
    }
  }

  /**
   * Richiede al server le motivazioni di annullamento
   */
  public async getCancelMotivation(): Promise<Array<Motivations>>{
    try {
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/food/cancel-motivation/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return [];
    }
  }

  /**
   * Richiede al server le calorie dell'animale
   * @param idAnimale
   */
  public async getCalories(idAnimale: string){
    const params = new URLSearchParams({ IDAnimale: idAnimale });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/calories/read' +
          '?' +
          params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
      
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  //#endregion

  //#region POST

  /**
   * crea un nuovo abbonamento di cibo sul db
   * @param idAnimale id dell'animale
   * @param idCategoriaAbbonamento id tipo di abbonamento
   * @param idSconto
   * @param scontoPercentuale
   * @param scontoFisso
   */
  public async createSubscription(idAnimale: string, note: string = '') {
    const params = JSON.stringify({
      IDAnimale: idAnimale,
      Note: note,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/create',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
    } catch (error) {
      console.error('Error creazione abbonamento', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return null;
    }
  }

  /**
   * Aggiunge i gusti all'ordine
   * @param idOrdine id dell'Ordine
   * @param gusti tutti i gusti dell'Ordine
   */
  public async addFlavors(idOrdine: string, gusti: any) {
    const params = JSON.stringify({
      IDOrdine: idOrdine,
      Gusti: gusti,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/flavors/create',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return true;
    } catch (error) {
      console.error('Error registrazione account', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();
      return false;
    }
  }

  /**
   * Aggiunge i gusti all'ordine
   * @param idOrdine id dell'Ordine
   * @param gusti tutti i gusti dell'Ordine
   */
  public async updateFlavors(idOrdine: string, gusti: any) {
    const params = JSON.stringify({
      IDOrdine: idOrdine,
      Gusti: gusti,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/flavors/update',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return true;
    } catch (error) {
      console.error('Error aggiornamento gusti', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return false;
    }
  }

  /**
   * Aggiorna la porzione dell'ordine non pagato
   * @param idOrdine id dell'Ordine
   * @param idPorzione id porzione
   * @param idSconto
   * @param scontoPercentuale
   * @param scontoFisso
   */
  public async updateSubscription(
    idOrdine: number,
    idPorzione: number | null = null
  ) {
    const params = JSON.stringify({
      IDOrdine: idOrdine,
      IDPorzione: idPorzione,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/update',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return true;
    } catch (error) {
      console.error('Error aggiornamento abbonamento', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return false;
    }
  }

  /**
   * Aggiorna la porzione dell'ordine attivo
   * @param idOrdine id dell'Ordine
   * @param idPorzione id porzione
   */
  public async updateActiveSubscription(
    idOrdine: number,
    idPorzione: number | null = null
  ) {
    const params = JSON.stringify({
      IDOrdine: idOrdine,
      IDPorzione: idPorzione,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/portion/update',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
    } catch (error) {
      console.error('Error aggiornamento abbonamento', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return null;
    }
  }

  /**
   * Aggiorna lo stato di un ordine
   * @param idOrdine id dell'Ordine
   * @param stato nuovo stato
   */
  public async updateStateSubscription(
    idOrdine: string,
    stato: string,
    idMotivazione: string | null = null
  ) {
    let params;

    if (idMotivazione) {
      params = JSON.stringify({
        IDOrdine: idOrdine,
        Stato: stato,
        IDMotivazione: idMotivazione,
      });
    } else {
      params = JSON.stringify({
        IDOrdine: idOrdine,
        Stato: stato,
      });
    }

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/state/update',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
    } catch (error) {
      console.error('Error aggiornamento abbonamento', error);
      // const toast = await this.toastController.create({
      //   message: 'Ops, qualcosa è andato storto!',
      //   duration: 2000,
      //   position: 'top',
      //   cssClass: 'custom-toast',
      // });
      // await toast.present();

      return null;
    }
  }

  /**
   * crea un nuovo ordine di cibo sul db
   * @param idOrdine id dell'Ordine
   * @param idPorzione id porzione
   * @param idSconto
   * @param scontoPercentuale
   * @param scontoFisso
   */
  public async updateSubscriptionNotes(idOrdine: number, note: string) {
    const params = JSON.stringify({
      IDOrdine: idOrdine,
      Note: note,
    });

    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/food/order/subscription/update',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
    } catch (error) {
      console.error('Error aggiornamento abbonamento', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return null;
    }
  }

  //#endregion
}
